// Cores derivadas da cor da marca (a cor do menu, em Configurações > Menu), no OKLCH: o matiz é o da marca e só a
// luminosidade (e um pouco da saturação) muda. Regras da rampa ordinal do validador de paleta (--ordinal): luminosidade
// sempre caindo do claro para o escuro, pelo menos 0,06 entre vizinhos e o passo mais claro com 2:1 de contraste no branco

const LIGHT_END_CONTRAST = 2.05 // passo mais claro: um pouco acima do piso de 2:1, para sobrar margem no arredondamento
const LIGHT_END_MAX_L = 0.82
const MIN_STEP_L = 0.0625 // um pouco acima do mínimo de 0,06 do validador
const LIGHT_CHROMA_RATIO = 0.55 // o passo mais claro fica menos saturado que a marca, como um tom pastel dela
const PREVIOUS_SHARE = 0.7 // ano anterior: 70% do caminho entre a marca e o passo mais claro
const PREVIOUS_MIN_DL = 0.12
const SHADE_DL = 0.1 // submenu: um passo bem visível abaixo da marca
const SHADE_MIN_L = 0.12 // marca quase preta: o submenu para aqui, sem virar preto
const SHADE_TEXT_CONTRAST = 4.5 // texto branco do submenu legível (WCAG AA)
// Tema escuro: os cartões ficam no #212529 (--app-surface do theme.css) e a marca precisa clarear para aparecer neles
export const DARK_SURFACE = '#212529'
const DARK_TEXT_CONTRAST = 4.5 // marca como texto, link e barra principal no escuro (WCAG AA)
const DARK_END_CONTRAST = 2.05 // passo mais escuro das rampas no escuro: o mesmo piso de 2:1 do claro, agora contra o fundo escuro
const DARK_MAX_L = 0.92

const toLinear = (value) => ((value <= 0.04045) ? value / 12.92 : ((value + 0.055) / 1.055) ** 2.4)
const toGamma = (value) => ((value <= 0.0031308) ? 12.92 * value : (1.055 * (value ** (1 / 2.4))) - 0.055)

// ' #680E8F ' -> '#680e8f'; null se não for um hex de 6 dígitos
export function normalizeHex(hex) {
  const value = String(hex || '').trim().replace(/^#/, '').toLowerCase()
  return /^[0-9a-f]{6}$/.test(value) ? `#${value}` : null
}

function parseHex(hex) {
  const value = normalizeHex(hex)
  if (!value) return null
  return [1, 3, 5].map((index) => parseInt(value.slice(index, index + 2), 16) / 255)
}

function linearToOklab([red, green, blue]) {
  const l = Math.cbrt((0.4122214708 * red) + (0.5363325363 * green) + (0.0514459929 * blue))
  const m = Math.cbrt((0.2119034982 * red) + (0.6806995451 * green) + (0.1073969566 * blue))
  const s = Math.cbrt((0.0883024619 * red) + (0.2817188376 * green) + (0.6299787005 * blue))
  return [
    (0.2104542553 * l) + (0.7936177850 * m) - (0.0040720468 * s),
    (1.9779984951 * l) - (2.4285922050 * m) + (0.4505937099 * s),
    (0.0259040371 * l) + (0.7827717662 * m) - (0.8086757660 * s),
  ]
}

function oklabToLinear([L, a, b]) {
  const l = (L + (0.3963377774 * a) + (0.2158037573 * b)) ** 3
  const m = (L - (0.1055613458 * a) - (0.0638541728 * b)) ** 3
  const s = (L - (0.0894841775 * a) - (1.2914855480 * b)) ** 3
  return [
    (4.0767416621 * l) - (3.3077115913 * m) + (0.2309699292 * s),
    (-1.2684380046 * l) + (2.6097574011 * m) - (0.3413193965 * s),
    (-0.0041960863 * l) - (0.7034186147 * m) + (1.7076147010 * s),
  ]
}

// '#680e8f' -> { l, c, h } (h em radianos); null se não for um hex de 6 dígitos
export function hexToOklch(hex) {
  const rgb = parseHex(hex)
  if (!rgb) return null
  const [l, a, b] = linearToOklab(rgb.map(toLinear))
  return { l, c: Math.hypot(a, b), h: Math.atan2(b, a) }
}

const inGamut = (linear) => linear.every((value) => (value >= -1e-6) && (value <= 1 + 1e-6))
const linearAt = ({ l, c, h }) => oklabToLinear([l, c * Math.cos(h), c * Math.sin(h)])

// { l, c, h } -> hex; fora do sRGB, a saturação cai (busca binária) e o matiz e a luminosidade ficam
export function oklchToHex({ l, c, h }) {
  let chroma = c
  if (!inGamut(linearAt({ l, c, h }))) {
    let low = 0
    let high = c
    for (let index = 0; index < 24; index += 1) {
      const middle = (low + high) / 2
      if (inGamut(linearAt({ l, c: middle, h }))) low = middle
      else high = middle
    }
    chroma = low
  }
  const channels = linearAt({ l, c: chroma, h }).map((value) => Math.round(Math.min(1, Math.max(0, toGamma(Math.min(1, Math.max(0, value))))) * 255))
  return `#${channels.map((value) => value.toString(16).padStart(2, '0')).join('')}`
}

// Luminância relativa (WCAG) de um hex; null se não for um hex de 6 dígitos
function luminance(hex) {
  const rgb = parseHex(hex)
  if (!rgb) return null
  const [red, green, blue] = rgb.map(toLinear)
  return (0.2126 * red) + (0.7152 * green) + (0.0722 * blue)
}

// Contraste WCAG da cor contra o branco
export function contrastOnWhite(hex) {
  const value = luminance(hex)
  if (value === null) return 1
  return 1.05 / (value + 0.05)
}

// Contraste WCAG entre duas cores
export function contrastRatio(hexA, hexB) {
  const a = luminance(hexA)
  const b = luminance(hexB)
  if ((a === null) || (b === null)) return 1
  return (Math.max(a, b) + 0.05) / (Math.min(a, b) + 0.05)
}

// Luminosidade do passo mais claro no matiz e saturação dados: a maior (até LIGHT_END_MAX_L) com contraste >= LIGHT_END_CONTRAST
function lightEndL(c, h) {
  let l = LIGHT_END_MAX_L
  while ((l > 0.3) && (contrastOnWhite(oklchToHex({ l, c, h })) < LIGHT_END_CONTRAST)) l -= 0.005
  return l
}

// Rampa ordinal de "count" cores, do claro para o escuro, no matiz da marca. Se a marca já é escura o bastante, ela é o
// último passo (exata); se é clara (um verde vivo, por exemplo), o último passo é um tom mais fechado dela, porque os passos
// precisam de luminosidade para se distinguir
export function brandRamp(primary, count, fallback) {
  const base = hexToOklch(primary) || hexToOklch(fallback)
  if (!base || (count < 1)) return []
  const lightC = base.c * LIGHT_CHROMA_RATIO
  const lightL = lightEndL(lightC, base.h)
  const darkL = Math.min(base.l, lightL - ((count - 1) * MIN_STEP_L))
  const exact = normalizeHex(primary) || normalizeHex(fallback)

  return Array.from({ length: count }, (_, index) => {
    if ((index === count - 1) && (darkL === base.l)) return exact
    const t = (count === 1) ? 1 : index / (count - 1)
    return oklchToHex({ l: lightL + ((darkL - lightL) * t), c: lightC + ((base.c - lightC) * t), h: base.h })
  })
}

// Ano anterior no gráfico mês a mês: um tom mais claro da marca (o ano base fica com a marca)
export function brandPrevious(primary, fallback) {
  const base = hexToOklch(primary) || hexToOklch(fallback)
  if (!base) return '#b07fcb'
  const c = base.c * 0.6
  const lightL = lightEndL(c, base.h)
  // nunca mais claro que o piso de contraste (marca clara, como um laranja), mas sempre um passo visível acima da marca
  const l = Math.min(base.l + Math.max(PREVIOUS_MIN_DL, (lightL - base.l) * PREVIOUS_SHARE), Math.max(lightL, base.l + MIN_STEP_L))
  return oklchToHex({ l, c, h: base.h })
}

// Fundo do submenu do menu lateral: a marca mais escura, no mesmo matiz. Marca clara (um laranja, por exemplo) desce mais,
// até o texto branco ter 4,5:1
export function brandShade(primary, fallback) {
  const base = hexToOklch(primary) || hexToOklch(fallback)
  if (!base) return '#460062'
  const shadeAt = (l) => oklchToHex({ l, c: base.c, h: base.h })
  let l = Math.max(base.l - SHADE_DL, SHADE_MIN_L)
  while ((l > SHADE_MIN_L) && (contrastOnWhite(shadeAt(l)) < SHADE_TEXT_CONTRAST)) l -= 0.005
  return shadeAt(l)
}

// Marca no tema escuro: o mesmo matiz, mais claro, até o texto ter 4,5:1 no fundo escuro. Serve para link, foco, contorno e
// a série principal dos gráficos; o preenchimento com texto branco (pílula ativa, menu) continua com a marca
export function brandOnDark(primary, fallback) {
  const base = hexToOklch(primary) || hexToOklch(fallback)
  if (!base) return '#c88be6'
  const tintAt = (l) => oklchToHex({ l, c: base.c, h: base.h })
  let l = base.l
  while ((l < DARK_MAX_L) && (contrastRatio(tintAt(l), DARK_SURFACE) < DARK_TEXT_CONTRAST)) l += 0.005
  return tintAt(l)
}

// Luminosidade do passo mais escuro no escuro: a menor com contraste >= DARK_END_CONTRAST contra o fundo escuro
function darkEndL(c, h) {
  let l = 0.3
  while ((l < DARK_MAX_L) && (contrastRatio(oklchToHex({ l, c, h }), DARK_SURFACE) < DARK_END_CONTRAST)) l += 0.005
  return l
}

// Rampa ordinal no tema escuro: o primeiro passo é o mais perto do fundo (escuro, 2:1) e o último, o mais forte (a marca do
// escuro), como no claro, em que o primeiro passo é o mais perto do fundo branco. Mesmas regras: 0,06 de luminosidade entre
// vizinhos e o matiz da marca
export function brandRampDark(primary, count, fallback) {
  const base = hexToOklch(primary) || hexToOklch(fallback)
  if (!base || (count < 1)) return []
  const top = hexToOklch(brandOnDark(primary, fallback))
  const lowC = base.c * LIGHT_CHROMA_RATIO
  const lowL = darkEndL(lowC, base.h)
  const highL = Math.max(top.l, lowL + ((count - 1) * MIN_STEP_L))

  return Array.from({ length: count }, (_, index) => {
    const t = (count === 1) ? 1 : index / (count - 1)
    return oklchToHex({ l: lowL + ((highL - lowL) * t), c: lowC + ((base.c - lowC) * t), h: base.h })
  })
}

// Ano anterior no tema escuro: um tom da marca mais perto do fundo que a série principal (brandOnDark), sem passar do piso de 2:1
export function brandPreviousDark(primary, fallback) {
  const base = hexToOklch(primary) || hexToOklch(fallback)
  if (!base) return '#7d4c98'
  const top = hexToOklch(brandOnDark(primary, fallback))
  const c = base.c * 0.6
  const l = Math.max(darkEndL(c, base.h), top.l - PREVIOUS_MIN_DL - MIN_STEP_L)
  return oklchToHex({ l: Math.min(l, top.l - MIN_STEP_L), c, h: base.h })
}
